/**
 * WebSocket hook for Video Call events
 */

import { useEffect, useCallback } from 'react';
import { useWebSocketStatus } from '@/context/WebSocketContext';
import type { VideoCallSocketEvents } from '@/api/types/videocall.types';
import { message } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { VIDEOCALLS_QUERY_KEY } from './useVideoCall';

interface UseVideoCallSocketOptions {
  enabled?: boolean;
  onCallStarted?: (data: VideoCallSocketEvents['video_call.started']) => void;
  onCallEnded?: (data: VideoCallSocketEvents['video_call.ended']) => void;
}

export const useVideoCallSocket = (options: UseVideoCallSocketOptions = {}) => {
  const { enabled = true, onCallStarted, onCallEnded } = options;
  const { socket, connected } = useWebSocketStatus();
  const queryClient = useQueryClient();

  const handleCallStarted = useCallback(
    (data: VideoCallSocketEvents['video_call.started']) => {
      console.log('Video call started:', data);

      // Show notification
      message.info({
        content: `${data.started_by.name} started a video call`,
        duration: 5,
        onClick: () => {
          window.open(`/apps/video-call?room=${data.room_name}`, '_blank');
        },
      });

      // Invalidate active meetings query
      queryClient.invalidateQueries({
        queryKey: [VIDEOCALLS_QUERY_KEY, 'active'],
      });

      // Call custom handler if provided
      onCallStarted?.(data);
    },
    [queryClient, onCallStarted]
  );

  const handleCallEnded = useCallback(
    (data: VideoCallSocketEvents['video_call.ended']) => {
      console.log('Video call ended:', data);

      // Invalidate queries
      queryClient.invalidateQueries({
        queryKey: [VIDEOCALLS_QUERY_KEY],
      });

      // Call custom handler if provided
      onCallEnded?.(data);
    },
    [queryClient, onCallEnded]
  );

  useEffect(() => {
    if (!socket || !connected || !enabled) return;

    socket.on('video_call.started', handleCallStarted);
    socket.on('video_call.ended', handleCallEnded);

    return () => {
      socket.off('video_call.started', handleCallStarted);
      socket.off('video_call.ended', handleCallEnded);
    };
  }, [socket, connected, enabled, handleCallStarted, handleCallEnded]);

  return { socket, connected };
};
